import { useCallback, useMemo } from "react";
import { useTenant } from "hooks/settings";
import { backendApi } from "src/store/api/enhancedApi";
import { useAppDispatch } from "src/store/store";
import {
  quantisedWindow,
  useLoadedWindow,
  type Chunk,
  type ChunkData,
} from "components/scrapTracking/helpers";
import { HeatSeries } from "src/store/api/generatedApi";

interface HeatsChunk {
  series: HeatSeries[];
  available_ignored_material_sets: string[][];
}

const HEATS_CHUNK_SIZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 day chunks
const HEATS_CHUNK_BUFFER = 1;

type UseHeatsDataResult = Readonly<{
  data: HeatsChunk;
  chunks: ChunkData<HeatsChunk>[];
  isLoading: boolean;
}>;

export const useHeatsData = (start: Date, end: Date): UseHeatsDataResult => {
  const tenant = useTenant();
  const dispatch = useAppDispatch();

  const fetchChunk = useCallback(
    async (chunk: Chunk): Promise<HeatsChunk> => {
      const subscription = dispatch(
        backendApi.endpoints.getStrategyDashboardHeats.initiate({
          tenantName: tenant,
          start: new Date(chunk.start).toISOString(),
          end: new Date(chunk.end).toISOString(),
        })
      );

      try {
        const result = await subscription.unwrap();
        return {
          series: result.series,
          available_ignored_material_sets:
            result.available_ignored_material_sets,
        };
      } finally {
        subscription.unsubscribe();
      }
    },
    [dispatch, tenant]
  );

  const quantised = useMemo(
    () =>
      quantisedWindow(
        { start: start.getTime(), end: end.getTime() },
        HEATS_CHUNK_SIZE_MS,
        HEATS_CHUNK_BUFFER
      ),
    [start, end]
  );

  const { chunks } = useLoadedWindow(quantised, fetchChunk);

  // Merge all successful chunks
  const mergedData = useMemo(() => {
    const allSeries: HeatSeries[] = [];
    const allIgnoredMaterialSets: string[][] = [];

    chunks.forEach((chunk) => {
      if (chunk.value.status === "success") {
        allSeries.push(...chunk.value.data.series);
        // Collect unique ignored material sets
        chunk.value.data.available_ignored_material_sets.forEach((set) => {
          const exists = allIgnoredMaterialSets.some(
            (existing) =>
              existing.length === set.length &&
              existing.every((val, idx) => val === set[idx])
          );
          if (!exists) {
            allIgnoredMaterialSets.push(set);
          }
        });
      }
    });

    // Sort by start_timestamp and dedupe by heat_id
    const seen = new Set<string>();
    const dedupedSeries = allSeries
      .sort(
        (a, b) =>
          new Date(a.start_timestamp).getTime() -
          new Date(b.start_timestamp).getTime()
      )
      .filter((h) => {
        if (seen.has(h.heat_id)) return false;
        seen.add(h.heat_id);
        return true;
      });

    return {
      series: dedupedSeries,
      available_ignored_material_sets: allIgnoredMaterialSets,
    };
  }, [chunks]);

  return {
    data: mergedData,
    chunks,
    isLoading: chunks.some((c) => c.value.status === "loading"),
  };
};
